import { AssetType, SLIDE_DATA_STATE_FIELDS, TaskState, THUMBNAIL_SIZES, UndoType } from "common/constants";
import { getSlideText } from "common/utils/slideText";
import { isOfflinePlayer, isRenderer } from "js/config";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import Thumbnails from "js/core/models/thumbnails";
import { getExperiments } from "js/core/services/experiments";
import NotificationsService from "js/core/services/notifications";
import pusher from "js/core/services/pusher";
import Adapter from "js/core/storage/adapter";
import DummyAdapter from "js/core/storage/dummyAdapter";
import themeManager from "js/core/themeManager";
import { loadImage } from "js/core/utilities/promiseHelper";
import { app } from "js/namespaces";
import { ShowDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { _, Backbone } from "js/vendor";
import React, { setGlobal } from "reactn";
import Profiler from "../profiler";
import SlidesAdapter from "../storage/slidesAdapter";
import StorageModel from "../storage/storageModel";
import { downloadFromUrl, filenameForExport, trackActivity } from "../utilities/utilities";
import { ds } from "./dataService";
import { presentations as presentationsApi } from "apis-v2/callables";
import { slides as slidesApi } from "apis-v2/callables";
import { PresentationActivityType } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import { WorkspaceResourceType } from "common/interfaces";
import { findElementsWithImages } from "common/utils/findElementsWithImages";
import { getSlideContentId, isSharedSlideInstance, resolveSharedSlideInstance, SHARED_SLIDE_INHERITED_FIELDS } from "common/utils/sharedSlideInstances";

const logger = getLogger(LogGroup.SLIDE);

const SHARED_SLIDE_INHERITED_FIELDS_SET = new Set(SHARED_SLIDE_INHERITED_FIELDS);

/**
 * Library slides loaded to resolve the instances pointing at them, keyed by library slide id.
 *
 * A deck that uses the same shared slide several times resolves every occurrence through one
 * entry here, so the slide is fetched once and every instance sees the same model — which is
 * also what keeps a live edit to the shared slide showing up on all of them at once.
 */
const librarySlideCache = new Map();

function loadLibrarySlide(librarySlideId, presentation, autoSync) {
    if (!librarySlideCache.has(librarySlideId)) {
        librarySlideCache.set(librarySlideId, (async () => {
            const librarySlide = new Slide(
                { id: librarySlideId },
                {
                    autoLoad: false,
                    // The read is authorized through this deck, which is what lets the deck's own
                    // viewers resolve the shared slide — see getSlide() in
                    // apis-v2/slides/handlers.ts
                    presentation,
                    // Follows the instance rather than defaulting to true. A viewer who only has
                    // the deck (a share link, a non-collaborator) can read the library slide
                    // through the deck, but cannot authorize the private-slide-{id} channel
                    // that autoSync subscribes to before the read — and that subscription failing
                    // is fatal, so it would leave the slide blank for exactly those viewers.
                    // Occurrences of one shared slide live in one deck, so they agree on this.
                    autoSync
                }
            );

            try {
                await librarySlide.load();
            } catch (err) {
                // Cached promises are shared, so a failure must not stick around and fail every
                // later instance too
                librarySlideCache.delete(librarySlideId);
                throw err;
            }

            return librarySlide;
        })());
    }

    return librarySlideCache.get(librarySlideId);
}

const profiler = Profiler.create({
    name: "Slide",
    type: Profiler.AVG_LOAD_TIME,
    warnTime: 400
});

const nonTextKeys = {
    content_type: true,
    content_value: true
};

const SOURCE = {
    local: 0,
    remote: 1,
    server: 2,
    initialize: 3
};

const Slide = StorageModel.extend({
    root: "slides",
    profiler: profiler,
    readonly: isOfflinePlayer,
    hasChanges: false,
    // Has to be set to true in order to make sure
    // the initial model is always normalized by
    // the adapter before firing the load event
    ensurePersisted: true,

    createAdapter: function(options) {
        return new SlidesAdapter({ ...options, model: this });
    },

    getDummyAdapterOptions() {
        return {
            removeTrailingNullsFromArrays: true
        };
    },

    initialize: function(model, options = {}) {
        this.type = "Slide";
        this.destroyed = false;
        this.dataStateIndex = 0;
        this.once("load", () => {
            this.dataStateIndex = 0;
            this._updateDataState({ source: SOURCE.initialize });
        });
        this.presentation = options.presentation;
    },

    isMigrated: function() {
        if (this.adapter instanceof SlidesAdapter) {
            return !this.adapter.fallbackToCommandsAdapter;
        }

        return false;
    },

    getReadOnlyClone: function() {
        const clone = new Backbone.Model();

        clone.load = () => {
            if (this.loadPromise) {
                return this.loadPromise;
            }

            return this.load()
                .then(() => {
                    clone.trigger("load", clone, {});
                    return clone;
                });
        };
        clone.commit = () => { };
        clone.finishedEditing = () => Promise.resolve();
        clone.destroy = () => { };
        clone.update = () => { };
        clone.presentation = this.presentation;
        clone.updatePromise = Promise.resolve();

        const proxyProp = propName => {
            Object.defineProperty(clone, propName, {
                get: () => this[propName]
            });
        };
        proxyProp("loaded");
        proxyProp("isLoaded");
        proxyProp("presentation");

        const syncCloneDataState = () => {
            clone.dataStateIndex = this.dataStateIndex ?? 0;
            clone.dataState = _.cloneDeep(this.dataState ?? {});
            clone.trigger("updateDataState", clone, {});
        };

        const syncCloneAttributes = (model = this, options = {}) => {
            clone.set(_.cloneDeep(this.getContentAttributes()));
            clone.trigger("change", clone, {});

            if (options?.isCommit) {
                syncCloneDataState();
            }
        };

        const forceSyncClones = () => {
            syncCloneAttributes(this, { isCommit: true });
        };

        clone.discardChanges = () => {
            clone.dataState = _.cloneDeep(this.dataState ?? {});
        };

        syncCloneAttributes();
        syncCloneDataState();

        this.on("change", syncCloneAttributes);
        this.on("updateDataState", syncCloneDataState);
        this.on("forceSyncClones", forceSyncClones);

        clone.disconnect = () => {
            this.off("change", syncCloneAttributes);
            this.off("updateDataState", syncCloneDataState);
        };

        return clone;
    },

    hasNextState: function() {
        const states = this.get("states");
        return states && this.dataStateIndex + 1 < states.length;
    },

    nextState: function() {
        if (!this.hasNextState()) {
            return;
        }
        this.dataStateIndex++;
        this._updateDataState({ source: SOURCE.local });
    },

    generateLoadPromise(...args) {
        return StorageModel.prototype.generateLoadPromise.call(this, ...args).then(async () => {
            await this.loadLibrarySlide();

            const templateId = this.get("template_id");
            const version = this.get("version");

            // Hack to mitigate the bug that we had in ppt importer when it wasn't setting
            // version on the generated slide models
            if (templateId === "ppt-slide" && !version) {
                this.update({ version: 9 }, { save: false });
            }

            return this;
        });
    },

    /**
     * An instance slide is an extra occurrence of a shared slide and stores no content of its own,
     * so it resolves its content from the library slide it points at. The library slide is kept
     * beside the attributes rather than merged into them: the instance stays a thin document that
     * only records where it sits in the deck, and nothing borrowed can be written back to it.
     */
    async loadLibrarySlide() {
        if (!this.isSharedSlideInstance()) {
            return;
        }

        // A slide built without a backing store — the offline player, the renderer, a preview —
        // is handed its attributes with the shared content already resolved into them, and has
        // no way to read the library slide anyway. Its dummy adapter would happily "load" an
        // empty one and resolving against that would strip the content it was given.
        if (this.adapter instanceof DummyAdapter) {
            return;
        }

        const librarySlideId = this.get("libraryItemId");

        let librarySlide;
        try {
            librarySlide = await loadLibrarySlide(librarySlideId, this.presentation, this.autoSync);
        } catch (err) {
            // The instance renders empty rather than failing to load. This is reachable without
            // anything being broken: the library slide may have been deleted from the workspace,
            // or the viewer may have lost access to it since the deck was shared.
            logger.error(err, "[Slide] loadLibrarySlide() failed, rendering the slide without its shared content", { id: this.id, librarySlideId });
            return;
        }

        if (this.destroyed) {
            return;
        }

        this.librarySlide = librarySlide;

        // Edits to the shared slide have to reach every occurrence of it in the deck. The library
        // slide keeps its own connection, so this only has to mirror what it already heard.
        this._onLibrarySlideChange = () => this._updateDataState({ source: SOURCE.remote });
        this.librarySlide.on("updateDataState", this._onLibrarySlideChange);

        this._reloadDataState({ source: SOURCE.local, silent: true });
    },

    _disconnectLibrarySlide() {
        if (this._onLibrarySlideChange) {
            this.librarySlide?.off("updateDataState", this._onLibrarySlideChange);
            this._onLibrarySlideChange = null;
        }
        this.librarySlide = null;
    },

    /**
     * This slide's attributes with the content it resolves to filled in — the library slide's
     * content for an instance, its own otherwise, and its own identity and place in the deck
     * either way. Callers that render, clone or export a slide want these rather than the raw
     * attributes, which for an instance carry no content at all.
     */
    getContentAttributes: function() {
        return this.librarySlide
            ? resolveSharedSlideInstance(this.attributes, this.librarySlide.attributes)
            : this.attributes;
    },

    /**
     * Fields an instance borrows from its library slide are served from there. Everything else —
     * notably the slide's own id, its place in the deck and its change bookkeeping — stays its own.
     */
    get: function(key) {
        if (this.librarySlide && SHARED_SLIDE_INHERITED_FIELDS_SET.has(key)) {
            return this.librarySlide.get(key);
        }

        return StorageModel.prototype.get.call(this, key);
    },

    setDataState: function(index) {
        if (!this.loaded) {
            return;
        }
        this.dataStateIndex = index;
        this._updateDataState({ source: SOURCE.local });
    },

    debug: function() {
        /* eslint-disable no-console */
        console.log("-----------------------------------------------");
        console.log("Slide:" + this.id + " index: " + this.presentation.getSlideIndex(this.id));
        console.log("Template: " + this.get("template_id"));
        console.log(JSON.stringify(this.get("states")[0], null, 2));
        /* eslint-disable no-console */
    },

    handleRemoteChange: function(type, data) {
        const changeSet = StorageModel.prototype.handleRemoteChange.call(this, type, data, false);

        // The initialize type is handled when the slide loads, so skip double firing here.
        if (type === Adapter.TYPE.initialize) {
            return;
        }

        // What this slide resolves to has changed. Deleting a shared slide detaches every extra
        // occurrence of it in place, writing the content onto them and clearing the pointer, so a
        // slide that was reading from the library slide a moment ago now owns that content itself.
        if (changeSet.hasUpdates && "libraryItemId" in changeSet.update) {
            this._disconnectLibrarySlide();
            this.loadLibrarySlide()
                .then(() => this._updateDataState({ source: SOURCE.remote }))
                .catch(err => logger.error(err, "[Slide] handleRemoteChange() failed to re-resolve the shared slide", { id: this.id }));
        }

        if (changeSet.hasUpdates) {
            this._updateDataState({ source: this._isDataStateChangeSet(changeSet) ? SOURCE.remote : SOURCE.server });
        }
    },

    update: function(attrs, options = {}) {
        const { saveUndo, forceUndo } = options;

        const originalAttrs = _.cloneDeep(this.attributes);

        if (isRenderer || window.isPlayer) {
            const targetObject = {};
            Error.captureStackTrace(targetObject);
            logger.warn(`[Slide] trying to update when data should be read only, trace for debugging: ${targetObject.stack}`, { id: this.id });
            options = Object.assign({}, options, { save: false });
        }

        // These values are set and returned by the server or the model, so we won't allow changing them from the outsize of the model
        const ensureValueNotChanged = key => {
            if (options.removeMissingKeys || key in attrs) {
                attrs[key] = this.get(key);
            }
        };
        ["_changeId", "modifiedAt"].forEach(ensureValueNotChanged);

        const changeSet = StorageModel.prototype.update.call(this, attrs, options);
        if (!options.isCommit && changeSet.hasUpdates && this._isDataStateChangeSet(changeSet)) {
            this._updateDataState(Object.assign(options, { source: SOURCE.local }));
        }

        if (changeSet.hasUpdates && !options.isFromMigration) {
            const updatedKeys = Object.keys(changeSet.update);
            const ignoreKeys = ["modifiedAt", "assignedUser", "status", "hasBeenViewed"];
            if (updatedKeys.length !== 1 || !ignoreKeys.includes(updatedKeys[0])) {
                this.hasChanges = true;
            }
        }

        if ((changeSet.hasUpdates || forceUndo) && saveUndo) {
            const { undoOldState = {}, undoNewState = {} } = options;

            app.undoManager.set(
                UndoType.SLIDE_DATA,
                this.id,
                {
                    attributes: originalAttrs,
                    ...undoOldState
                },
                {
                    attributes: this.attributes,
                    ...undoNewState
                },
                {
                    ...(options.undoOptions ?? {}),
                    presentation: this.presentation
                },
            );
        }

        return changeSet;
    },

    _isDataStateChangeSet: function(changeSet) {
        return SLIDE_DATA_STATE_FIELDS.some(key => key in changeSet.original);
    },

    _reloadDataState: function(options) {
        this.dataState = getSlideDataStateFromAttributes(this.getContentAttributes(), this.dataStateIndex);

        if (!options.silent) {
            this.trigger("updateDataState", this, Object.assign(options, {
                remoteChange: options.source === "remote",
                initialize: options.source === "initialize"
            }));
        }
    },

    _updateDataState: function(options) {
        switch (options.source) {
            case SOURCE.local:
                this._reloadDataState(options);
                break;
            case SOURCE.remote:
                this._reloadDataState(options);
                break;
            case SOURCE.initialize:
                this._reloadDataState(options);
                break;
            case SOURCE.server:
                break;
            default:
                logger.warn("[Slide] Source unknown: " + options.source, { id: this.id });
                break;
        }
    },

    getAttributesFromDataState: function() {
        // dataState contains element models as well as other props that are part of
        // the slide model so we merge them into the slide model but we'll remove elements
        // from the root and add them to the states prop
        //
        // NOTE: we have to clone deep this.attributes to make sure we don't mutate them
        // when mutating the data object
        let data = { ..._.cloneDeep(this.attributes), ...this.dataState };
        delete data.elements;

        // States may have been removed if there were no elements on the slide
        if (!data.states) {
            data.states = [this.dataState.elements];
            // Force data state index to zero since we have only one state
            this.dataStateIndex = 0;
        } else {
            // Assigning elements to the current data state index
            data.states[this.dataStateIndex] = this.dataState.elements;
        }

        // This clone deep is mandatory because the update function in some cases
        // can mount subobjects of the data objects to the model's attributes
        // in a non-mutation safe manner so we can end up with data state having
        // references to the model's attributes
        data = _.cloneDeep(data);

        return data;
    },

    /**
     * This will push the dataState to the model.
     * @param silent If true, no events will be emitted. this can prevent feedback loops in cases where we
     * don't want the display to update.
     */
    commit: function(options = {}) {
        const data = this.getAttributesFromDataState();

        // Special case to avoid unnecessary slide model updates
        const annotations = data.states[this.dataStateIndex].annotations;
        const prevAnnotations = this.attributes.states ? this.attributes.states[this.dataStateIndex]?.annotations : null;
        if (_.isEqual(annotations, { items: [], connections: { items: [] } }) && !prevAnnotations) {
            data.states[this.dataStateIndex].annotations = null;
        }

        // Preserve the original state in case we need to save an undo step
        const saveUndo = app.undoManager && options.undo !== false;
        const forceUndo = saveUndo && options.forceUndo;

        // Update the model
        this.update(
            data,
            {
                removeMissingKeys: true,
                isCommit: true,
                isFromMigration: _.defaultTo(options.isFromMigration, false),
                saveUndo,
                forceUndo
            }
        );

        return this.dataState;
    },

    discardChanges: function() {
        this._updateDataState({ source: SOURCE.local, silent: true });
    },

    disconnect: async function() {
        this.off();
        this._disconnectLibrarySlide();

        // Make sure we finished editing before disconnecting
        await this.finishedEditing();
        this.adapter.disconnect();
    },

    resetSlideImage: async function(isGenerating = true) {
        const update = resetSlideImage(this.attributes, isGenerating);
        this.update(update);
        await this.updatePromise;
    },

    destroy(options) {
        //Call Backbone's destroy instead of the StorageModel's destroy so we can call our delete api instead.
        Backbone.Model.prototype.destroy.call(this, options);
        this.destroyed = true;
        this._disconnectLibrarySlide();
        this.adapter.disconnect();
        //delete the slide via an endpoint.
        if ((!options || !options.remoteChange) && !this.disconnected) {
            return slidesApi.deleteSlide({ id: this.id });
        } else {
            return Promise.resolve({});
        }
    },

    finishedEditing: async function() {
        if (this.hasChanges && !this.isDetached) {
            this.hasChanges = false;

            const timestamp = new Date().getTime();

            try {
                StorageModel.prototype.update.call(this, { modifiedAt: timestamp });
                await this.updatePromise;

                if (this.presentation && !this.presentation.disconnected) {
                    // If the slide is the first slide of its presentation
                    if (this.presentation.getSlideIndex(this.id) === 0) {
                        this.presentation.update({ firstSlideModifiedAt: timestamp });
                        await this.presentation.updatePromise;
                    }

                    // Record EDITED activity immediately for "last edited" in library (don't wait for notifications processor)
                    presentationsApi.recordActivity({
                        id: this.presentation.id,
                        activity: PresentationActivityType.EDITED,
                        activityData: { slideId: this.id }
                    }).catch(err => logger.error(err, "[slide] finishedEditing() recordActivity EDITED failed", { id: this.id }));

                    // Fire and forget
                    NotificationsService.notifyOnSlideEdited(this.presentation.id, this.id)
                        .catch(err => logger.error(err, "[slide] finishedEditing() NotificationsService.notifyOnSlideEdited() failed", { id: this.id }));
                }
            } catch (err) {
                logger.error(err, "[Slide] modifiedAt set failed", { id: this.id });

                ShowWarningDialog({
                    title: "Something went wrong.",
                    message: <>
                        <span>
                            There may be a temporary problem with your internet connection,<br />
                            or the permissions for this presentation may have changed.<br />
                            If the problem persists, please contact <a href="mailto:support@beautiful.ai">support@beautiful.ai</a>
                        </span>
                    </>,
                    acceptCallback: async () => {
                        if (this.presentation.invalid) {
                            //to ensure the presentation library shows the correct permission for the presentation refetch the user permissions
                            await this.presentation.getUserPermissions(true);
                        }
                        app.router.navigate("/", { trigger: true });
                    },
                });
            } finally {
                const {
                    template_recommendations: { enabled: hasTemplateRecommendations }
                } = await getExperiments(["template_recommendations"]);

                trackActivity("Slide", "ContentModified", null, null, {
                    "slide_id": this.id,
                    "slides_created": 0,
                    "source_presentation_id": this.presentation?.get("sourcePresentationId") || "",
                    "source_presentation_name": this.presentation?.get("sourcePresentationName") || "",
                    "is_from_recommended_template": this.presentation?.get("metadata")?.isFromRecommendedTemplate ?? false,
                    "object": "",
                    "object_label": "",
                    "action": "",
                    "experiment_id": "5FC18A79E182FE7C764425D4F852F501",
                    "experiment_group_assignment": hasTemplateRecommendations ? "variant-a" : "control"
                }, { skipAmplitude: true });
            }
        }
    },

    getSlideText() {
        return getSlideText(this.dataState.elements);
    },

    hasMultipleStages() {
        return this.get("template_id") === "textgrid_carousel";
    },

    getPlaybackStageCount() {
        if (this.hasMultipleStages()) {
            return this.dataState?.elements.primary.items?.length ?? 1;
        }
        return 1;
    },

    async getThumbnailUrl(presetationOrLinkId = null, size = "small", stageIndex = 0, preventModelLoad = false, fallbackTimestamp = null, reloadCache = false) {
        if (!THUMBNAIL_SIZES[size]) {
            throw new Error(`Unknown thumbnail size ${size}`);
        }

        if (window.isOfflinePlayer) {
            const url = `/userData/thumbnails/${this.id}${stageIndex === 0 ? "" : `-${stageIndex}`}`;
            await loadImage(url);
            return url;
        }

        if (!this.loaded && !preventModelLoad) {
            await this.load();
        }

        let timestamp;
        if (this.loaded) {
            timestamp = this.get("modifiedAt");
        } else if (fallbackTimestamp) {
            timestamp = fallbackTimestamp;
        } else {
            timestamp = this.instantiatedAt;
        }

        // NOTE: this asks for the slide's own id even for an extra occurrence of a shared slide,
        // which owns no thumbnails. The API resolves it to the library slide's thumbnail file, so
        // the occurrences still share one render — and asking under the id the deck actually
        // references is what lets the permission check confirm the slide belongs to this deck.
        return Thumbnails.getSignedUrlAndLoad(this.id, timestamp, presetationOrLinkId, THUMBNAIL_SIZES[size].suffix, stageIndex, reloadCache);
    },

    async generateJpegs() {
        if (!this.disconnected) {
            await this.load();
        }

        const theme = await themeManager.loadThemeFromPresentation(this.presentation);

        const { channelId } = await Api.jpegs.post({
            // An instance slide carries no content of its own, so the renderer is handed the
            // content it resolves to — it receives a plain model and does no resolving itself.
            slideModel: this.getContentAttributes(),
            theme: theme.attributes,
            workspaceId: this.presentation.getWorkspaceId()
        });

        const { urls } = await pusher.waitForEvent(channelId, TaskState.FINISHED, TaskState.ERROR, 2 * 60 * 1000);
        return urls;
    },

    exportSlideToJpeg() {
        trackActivity("Presentation", "JPEGExport", null, null, {
            workspace_id: this.presentation.getWorkspaceId()
        }, { audit: true });
        setGlobal({
            isLoadingDialogOpen: true
        });

        const dialog = ShowDialog(ProgressDialog, {
            title: "Exporting Slide to JPEG...",
        });

        const exportSlide = async () => {
            const urls = await this.generateJpegs();

            for (const [idx, url] of urls.entries()) {
                await downloadFromUrl(url,
                    filenameForExport({
                        name: this.presentation.get("name"),
                        assetType: "jpeg",
                        slideNum: this.getIndex() + 1,
                        secondarySlideNum: idx
                    }));
            }

            setGlobal({
                isLoadingDialogOpen: false
            });
            dialog.props.closeDialog();
        };

        exportSlide()
            .catch(err => {
                setGlobal({
                    isLoadingDialogOpen: false
                });
                dialog.props.closeDialog();
                logger.error(err, "[Slide] exportSlide() failed", { id: this.id });
            });
    },

    getIndex() {
        return this.presentation.getSlideIndex(this.id);
    },

    async toObject() {
        const attrs = await StorageModel.prototype.toObject.call(this);
        // Loading thumbnails of all sizes
        await Promise.all(Object.keys(THUMBNAIL_SIZES).map(size => this.getThumbnailUrl(this.presentation.id, size)));
        return attrs;
    },

    toText: function() {
        const text = [];

        function toText(obj) {
            if (typeof obj === "string" && obj !== "") {
                text.push(obj);
            } else if (_.isObject(obj)) {
                Object.keys(obj).forEach(key => {
                    if (!nonTextKeys[key]) {
                        toText(obj[key]);
                    }
                });
            }
        }

        toText(this.get("states"));
        text.push(this.get("slide_notes"));
        return text.join(" ");
    },

    isLibrarySlide: function() {
        return this.has("libraryItemId");
    },

    /**
     * True when this slide is an extra occurrence of a shared slide in the deck: it has its
     * own id so the deck can list it separately, but its content lives on the library slide.
     */
    isSharedSlideInstance: function() {
        return isSharedSlideInstance({ id: this.id, libraryItemId: this.get("libraryItemId") });
    },

    /** The id of the slide that actually holds this slide's content. */
    getContentId: function() {
        return getSlideContentId({ id: this.id, libraryItemId: this.get("libraryItemId") });
    },

    getMetadata: function() {
        return this.get("metadata") ?? {};
    },

    updateMetadata: function(props) {
        const metadata = this.getMetadata();
        const updatedMetadata = { ...metadata, ...props };
        this.update({ metadata: updatedMetadata });
    },

    isLocked() {
        return this.isLibrarySlide();
    },

    hasAudibleVideoAsset: function() {
        let hasAudibleVideo = false;
        const checkForVideo = object => {
            if (!object) {
                return;
            }

            if (!_.isObject(object)) {
                return;
            }

            if (
                object.videoAssetId ||
                object.videoId ||
                (
                    (object.content_type === AssetType.STOCK_VIDEO || object.content_type === AssetType.VIDEO) &&
                    object.content_value
                )
            ) {
                if (!object.assetProps?.muted) {
                    hasAudibleVideo = true;
                    return;
                }
            }

            Object.keys(object).forEach(key => {
                return checkForVideo(object[key]);
            });
        };
        checkForVideo(this.get("states"));

        return hasAudibleVideo;
    }
});

const Slides = Backbone.Collection.extend({
    model: Slide,

    url: function() {
        return "slides";
    },

    initialize: function() {
        this.type = "Slides";
    }
});

function getSlideDataStateFromAttributes(attributes, dataStateIndex = 0) {
    // Make sure elements is defined even if there are no
    // states because the canvas will be mutating it and will
    // crash if it is not an object
    let elements = {};
    const states = attributes.states;
    if (states && states[dataStateIndex]) {
        elements = _.cloneDeep(states[dataStateIndex]);
    }

    // Layout and animations have to be mutation-safe and always defined as well
    const layout = _.cloneDeep(attributes.layout ?? {});
    const animations = _.cloneDeep(attributes.animations ?? {});

    // This will contain a set of data which will be manipulated
    // by the canvas, so we have to make sure that mutating it is safe
    // hence clone deep elements and layout
    const dataState = {
        elements,
        layout,
        animations
    };

    // Pick the rest of the keys
    SLIDE_DATA_STATE_FIELDS.forEach(key => {
        if (key === "states" || key in dataState) {
            return;
        }
        dataState[key] = _.cloneDeep(attributes[key]);
    });

    return dataState;
}

function resetSlideImage(attributes, isGenerating = true) {
    const newAttributes = _.cloneDeep(attributes);
    if (newAttributes.metadata?.generatedModel?.slideImage?.assetId) {
        newAttributes.metadata.generatedModel.slideImage.assetId = null;
        newAttributes.metadata.generatedModel.slideImage.isGenerating = isGenerating;
    }
    if (newAttributes.metadata?.generatedSlides?.length) {
        newAttributes.metadata.generatedSlides.forEach(slide => {
            if (slide.slideData?.slideImage?.assetId) {
                slide.slideData.slideImage.assetId = null;
                slide.slideData.slideImage.isGenerating = isGenerating;
            }
        });
    }

    const elementsWithImages = findElementsWithImages(newAttributes);
    for (const element of elementsWithImages) {
        if (element.__generateImage?.isSlideImage) {
            element.__generateImage.isGenerating = isGenerating;
            element.content_value = null;
        }
    }

    return newAttributes;
}

function applySlideImage(attributes, slideImage) {
    const newAttributes = _.cloneDeep(attributes);
    if (!newAttributes.metadata) {
        newAttributes.metadata = {};
    }
    if (!newAttributes.metadata.generatedModel) {
        newAttributes.metadata.generatedModel = {};
    }
    if (!newAttributes.metadata.generatedModel.slideImage) {
        newAttributes.metadata.generatedModel.slideImage = {};
    }
    newAttributes.metadata.generatedModel.slideImage = _.cloneDeep(slideImage);

    const elementsWithImages = findElementsWithImages(newAttributes);
    for (const element of elementsWithImages) {
        if (element.__generateImage?.isSlideImage) {
            element.content_value = slideImage.assetId;
            element.__generateImage.isGenerating = false;
        }
    }

    return newAttributes;
}

export { getSlideDataStateFromAttributes, resetSlideImage, applySlideImage, Slide, Slides };
